import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PaymentOptions from '../payment_options/payment_options';
import classes from './classes.module.scss';
import { paymentSourcesTheme } from '../config/const';
import PaymentMethodsList from '../payment_methods_list/payment_methods_list';
import { getPurchaseSpriteUrl, loadSVGSprite } from '../sprites/loadSprite';
import {
  starSymbol, renderPaymentMethodsListTitle, renderdDetailsButton,
  getPurchaseURL, renderBoldPrice, renderToast, rendeExtraPriceLabel
} from './purchase_details_common_utils';
import UsePreviousValue from '../config/utils';
import { getPurchaseClickSource } from '../../app/components/purchases/purchases_utils';

function PurchaseDetailsCommon({
  clientAvailableCredit = '0',
  loadingOptions,
  paymentMethodListAnalyticsEvents,
  getCurrentLocalization,
  showPaymentSources,
  log,
  chatType,
  isContinue,
  renderCreditCardProcessing,
  onPurchaseDetailsScreenOpened,
  renderPayPalProcessing,
  paymentProcessing,
  hideAffiliatePromoPopupAction,
  options = [],
  addCreditCard,
  loadingSources,
  paymentSources,
  hidePaymentDetails,
  theme = paymentSourcesTheme.purchase,
  backArrowAction,
  onProcessing,
  forMobileBuyBtn,
  paymentMethodListActions,
  onMakeDefault,
  trackAddFundsScreenEvent,
  setInAppPaymentOptions,
  receivedMessageFromApp = {},
  setMessageToAppData,
  purchaseDetails,
  isPayg = false,
  renderCreditCardValidateProcessing,
  creditCardValidateError,
  trackPageVisit,
  loadPaymentOptionsOnClose,
  onPurchaseComplete,
  cancelChatOnPP,
  topBannerProps,
  trackPaymentMethodClickedEvent
}) {
  const { t } = useTranslation();
  const [detailsOpened, setDetailsOpened] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const history = useNavigate();
  const location = useLocation();
  const [needTrackAddFundsScreenEvent, setNeedTrackAddFundsScreenEvent] = useState(true);
  const { current } = paymentSources;
  const [currentState, setuCurrentState] = useState(current);
  const {
    bonus = 0, changingSource, clickSource, duration, price, purchaseType, url, expiresAt,
    diffPrice, isMinPurchase, requiredCredit, fees, advisorId, topupFlow
  } = purchaseDetails;

  const bannerProps = useMemo(() => topBannerProps, [topBannerProps.userId]);

  useEffect(() => {
    let isCleanedUp = false;
    const loadSprites = async () => {
      if (!isCleanedUp) {
        loadSVGSprite(getPurchaseSpriteUrl(), 'purchase-svg-sprite-container');
      }
    };

    // Small delay to prevent race condition with DOM
    const timeoutId = setTimeout(loadSprites, 10);

    // Cleanup function that prevents deletion
    return () => {
      isCleanedUp = true;
      clearTimeout(timeoutId);
      // DON'T clear sprites on cleanup in React 19
    };
  }, []);

  useEffect(() => {
    onPurchaseDetailsScreenOpened();
    return () => { if (loadPaymentOptionsOnClose) loadPaymentOptionsOnClose(); };
  }, []);

  useEffect(() => {
    if (current !== currentState) setuCurrentState(current);
    const prevCurrent = <UsePreviousValue value={ currentState } />;
    if (current && (prevCurrent.props.value === undefined && prevCurrent.props.value !== current)) {
      onPurchaseDetailsScreenOpened();
    }
  }, [current]);

  useEffect(() => {
    if (options && options.length && needTrackAddFundsScreenEvent && requiredCredit) {
      trackAddFundsScreenEvent(parseFloat(requiredCredit.toFixed(2)));
      setNeedTrackAddFundsScreenEvent(false);
    }
  }, [options, requiredCredit]);

  const renderPurchaseButtonSection = () => {
    const requiredCreditToFixed = (requiredCredit || 0).toFixed(2);
    const purchaseURLClickSource = getPurchaseClickSource(purchaseType, location);
    const purchaseURL = getPurchaseURL({
      url, requiredCredit: requiredCreditToFixed, purchaseType, clickSource: purchaseURLClickSource, advisorId, topupFlow
    });
    const paymentOptionsProps = {
      purchaseURL, type: purchaseType, purchaseAmount: price, chatType,
      requiredCredit: requiredCreditToFixed, isMinPurchase, bonus, paymentMethodListAnalyticsEvents,
      expiresAt, renderCreditCardProcessing, renderPayPalProcessing, paymentProcessing,
      hideAffiliatePromoPopupAction, getCurrentLocalization, options, loadingOptions,
      loading: loadingSources, addCreditCard, paymentSources, theme, changingSource, isContinue,
      forMobileBuyBtn, onPurchaseDetailsScreenOpened, showPaymentSources, setInAppPaymentOptions,
      fees, isPayg, renderCreditCardValidateProcessing, creditCardValidateError, onProcessing, onPurchaseComplete, cancelChatOnPP, trackPaymentMethodClickedEvent, addSource: paymentMethodListActions.addSource, topupFlow, advisorId
    };
    return <PaymentOptions { ...paymentOptionsProps } />;
  };

  const renderDetailsSection = () => {
    if (!detailsOpened) return <div />;
    const requiredFundsString = duration
      ? `${ t('chat.required_funds_with_min', { mins: parseFloat(duration) / 60 }) } $${ (price || 0).toFixed(2) }`
      : `${ t('chat.required_funds') } $${ (price || 0).toFixed(2) }`;
    return (
      <div className={ classes.detailsView }>
        <div className={ classes.divider } />
        <div className={ classes.detailsViewText }>
          { requiredFundsString }
        </div>
        <div className={ classes.detailsViewText }>
          { `${ t('chat.current_balance') } $${ parseFloat(clientAvailableCredit).toFixed(2) }`}
        </div>
        <div className={ classes.divider } />
        <div className={ classes.detailsViewText }>
          <b>{ `${ t('chat.additional_funds_required') } $${ diffPrice.toFixed(2) }` }</b>
          { starSymbol(12, diffPrice) }
        </div>
      </div>
    );
  };

  const updateDefault = (source) => {
    const makeDefParams = {
      id:source.id, kind:source.kind, type:source?.context?.type, clickSource
    };
    onMakeDefault(history, location, makeDefParams);
    setShowToast(true);
    setTimeout(() => { setShowToast(false); }, 3000);
  };

  const renderPaymentMethodsList = () => {
    const paymentMethodsListProps = {
      ...paymentMethodListActions, analyticsEvents: paymentMethodListAnalyticsEvents,
      paymentSources, loading: loadingSources, theme: paymentSourcesTheme.purchase,
      log, updateDefault, changingSource, clickSource, onMakeDefault, setInAppPaymentOptions,
      receivedMessageFromApp, setMessageToAppData, isPayg, trackPageVisit
    };
    return <PaymentMethodsList { ...paymentMethodsListProps } />;
  };

  const renderPurchaseDetailsCommon = () => (
    <div className={ classes.container } data-testid="purchase-details-common-container">
      <div className={ classes.containerWithIcon }>
        <div className={ classes.dialogContainer }>
          { renderPaymentMethodsListTitle({
            purchaseType, backArrowAction, hidePaymentDetails, theme, diffPrice, t, bonus,
            receivedMessageFromApp, setMessageToAppData, isPayg, creditCardValidateError, loading: loadingSources
          }) }
          <div className={ classes.priceHolder }>
            <div className={ classes.flexCenteredDiv }>
              { renderBoldPrice({
                purchaseType, diffPrice, theme, isPayg, topBannerProps: bannerProps
              }) }
            </div>
            <div className={ classes.flexCenteredDiv }>
              {rendeExtraPriceLabel({
                options, price, bonus, t, purchaseType
              })}
            </div>
            <div className={ classes.flexCenteredDiv }>
              { renderdDetailsButton({
                purchaseType, setDetailsOpened, detailsOpened, t, isPayg
              }) }
            </div>
            { renderDetailsSection() }
          </div>
          { renderPurchaseButtonSection() }
        </div>
        {renderToast({
          fees, showToast, isPayg, t
        })}
      </div>
    </div>
  );

  if (changingSource) return renderPaymentMethodsList();
  return renderPurchaseDetailsCommon();
}

export default PurchaseDetailsCommon;
